import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { useAuthGate } from '../context/AuthGateContext';
import {
  MapPin, Heart, Share2, ArrowRight, CheckCircle,
  Building2, Loader, Wifi, Dumbbell, Car, Coffee, Sparkles,
  LayoutGrid, Map
} from 'lucide-react';
// mockData import removed — all listings come from the real API

const MapView = lazy(() => import('./MapView'));
const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_SIZE = 20;
const PLACEHOLDER = 'https://images.pexels.com/photos/1545743/pexels-photo-1545743.jpeg?auto=compress&cs=tinysrgb&w=600';

const getAmenityIcon = (amenity) => {
  if (amenity === 'WiFi') return <Wifi className="w-3 h-3" />;
  if (amenity === 'Gym') return <Dumbbell className="w-3 h-3" />;
  if (amenity === 'Parking') return <Car className="w-3 h-3" />;
  return <Coffee className="w-3 h-3" />;
};

// Normalise real API property to display shape
const normalise = (p, isReal) => isReal
  ? {
      id: p.id,
      name: p.name,
      location: `${p.city}, Australia`,
      city: p.city,
      address: p.address || '',
      street_address: p.street_address || '',
      suburb: p.suburb || '',
      postcode: p.postcode || '',
      state: p.state || '',
      country: p.country || 'Australia',
      lat: p.lat ?? null,
      lng: p.lng ?? null,
      price: `A$${p.price_per_week}`,
      price_per_week: p.price_per_week,
      period: 'week',
      image: p.images?.[0] || PLACEHOLDER,
      images: p.images || [],
      roomTypes: [p.room_type],
      amenities: p.amenities || [],
      walkingDistance: p.university_nearby ? `Near ${p.university_nearby}` : p.city,
      description: p.description || '',
      rating: null, reviews: null, isReal: true,
    }
  : { ...p, image: p.image, description: p.description || '', isReal: false };

const PropertyListings = ({ selectedCity = null, onClearCity }) => {
  const [favorites, setFavorites] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('rentalx_fav') || '[]')); }
    catch { return new Set(); }
  });
  const [copiedId, setCopiedId] = useState(null);
  const [shareMsg, setShareMsg] = useState('');
  const [realProps, setRealProps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [viewMode, setViewMode] = useState('all');
  const [displayMode, setDisplayMode] = useState('grid'); // 'grid' | 'map' // all | featured | foryou
  // Personalized feed state
  const [forYouLoading, setForYouLoading] = useState(false);
  const [forYouData, setForYouData] = useState(null); // { personalized, rankings, requirement }
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { requireAuth } = useAuthGate();

  const isStudent = user?.role === 'student' || (user && user.role !== 'landlord');

  useEffect(() => {
    setLoading(true);
    const cityParam = selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : '';
    // Fetch first page + true total count of active public listings
    Promise.all([
      fetch(`${API}/api/properties?limit=${PAGE_SIZE}&skip=0${cityParam}`).then(r => r.json()),
      fetch(`${API}/api/properties/count?${cityParam.replace(/^&/, '')}`).then(r => r.json()).catch(() => ({ total: 0 })),
    ])
      .then(([data, count]) => {
        setRealProps(Array.isArray(data) ? data : []);
        setTotal(typeof count?.total === 'number' ? count.total : (Array.isArray(data) ? data.length : 0));
      })
      .catch(() => { setRealProps([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [selectedCity]);

  const loadMore = () => {
    setLoadingMore(true);
    const cityParam = selectedCity ? `&city=${encodeURIComponent(selectedCity)}` : '';
    fetch(`${API}/api/properties?limit=${PAGE_SIZE}&skip=${realProps.length}${cityParam}`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data) && data.length) setRealProps(prev => [...prev, ...data]); })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  // Load personalized feed when "For You" tab selected
  useEffect(() => {
    if (viewMode !== 'foryou' || !token || forYouData) return;
    setForYouLoading(true);
    fetch(`${API}/api/ai/personalized-feed`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    })
      .then(r => r.ok ? r.json() : { personalized: false, rankings: [] })
      .then(data => setForYouData(data))
      .catch(() => setForYouData({ personalized: false, rankings: [] }))
      .finally(() => setForYouLoading(false));
  }, [viewMode, token]);

  const handleCardClick = (id, isReal) => {
    if (!user) { requireAuth(`/property/${id}`); return; }
    navigate(`/property/${id}`);
  };

  const toggleFav = (id, e) => {
    e.stopPropagation();
    setFavorites(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      localStorage.setItem('rentalx_fav', JSON.stringify([...next]));
      return next;
    });
  };

  const share = (p, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/property/${p.id}`;
    const doShare = () => {
      setCopiedId(p.id);
      setShareMsg('Link copied!');
      setTimeout(() => { setCopiedId(null); setShareMsg(''); }, 2500);
    };
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(doShare).catch(() => {
        const el = document.createElement('input');
        el.value = url; document.body.appendChild(el); el.select();
        document.execCommand('copy'); document.body.removeChild(el);
        doShare();
      });
    } else {
      const el = document.createElement('input');
      el.value = url; document.body.appendChild(el); el.select();
      document.execCommand('copy'); document.body.removeChild(el);
      doShare();
    }
  };

  // Always use real props — never show mock data to users
  const displayProps = realProps.map(p => normalise(p, true));

  const isEmpty = !loading && realProps.length === 0;

  return (
    <section id="properties" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          {shareMsg && (
            <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-400" />{shareMsg}
            </div>
          )}
          <h2 className="text-4xl md:text-5xl font-bold text-[#094f6f] mb-4">
            {selectedCity ? `Properties in ${selectedCity}` : 'Properties Across Australia'}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Student-verified accommodation from trusted landlords and fellow students
          </p>
          {selectedCity && (
            <button
              onClick={onClearCity}
              className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#094f6f] border border-[#094f6f] px-3 py-1 rounded-full hover:bg-sky-50 transition-colors"
            >
              <span>Showing: {selectedCity}</span>
              <span className="text-lg leading-none">&times;</span>
            </button>
          )}
        </div>

        {/* Toggle + View Mode */}
        <div className="flex items-center justify-between mb-10 flex-wrap gap-3">
          <div className="bg-gray-100 rounded-full p-1 flex gap-1 flex-wrap justify-center">
            {[
              ['all', 'Live Listings'],
              ['featured', 'Featured'],
              ...(isStudent && user ? [['foryou', '✨ For You']] : []),
            ].map(([val, label]) => (
              <button key={val} onClick={() => setViewMode(val)}
                className={`rounded-full px-5 py-2.5 text-sm font-medium transition-all ${
                  viewMode === val
                    ? val === 'foryou' ? 'bg-purple-600 text-white shadow-sm' : 'bg-[#094f6f] text-white shadow-sm'
                    : 'text-gray-600 hover:text-[#094f6f]'
                }`}>
                {label}{val === 'all' && total > 0 && ` (${total})`}
              </button>
            ))}
          </div>

          {/* Grid / Map toggle */}
          <div className="flex bg-gray-100 rounded-full p-1 gap-1">
            <button onClick={() => setDisplayMode('grid')} data-testid="listings-grid-toggle"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${displayMode === 'grid' ? 'bg-white text-[#094f6f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <LayoutGrid className="w-4 h-4" /> Grid
            </button>
            <button onClick={() => setDisplayMode('map')} data-testid="listings-map-toggle"
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all ${displayMode === 'map' ? 'bg-white text-[#094f6f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
              <Map className="w-4 h-4" /> Map
            </button>
          </div>
        </div>

        {/* Map View — split layout: list + map (stacks on mobile) */}
        {displayMode === 'map' && viewMode !== 'foryou' && (
          <div className="flex flex-col lg:flex-row gap-4 lg:h-[620px]">
            {/* Left: scrollable property list */}
            <div className="w-full lg:w-80 flex-shrink-0 overflow-y-auto space-y-3 pr-1 max-h-[320px] lg:max-h-none">
              {loading ? (
                <div className="flex justify-center pt-10"><Loader className="w-6 h-6 text-[#094f6f] animate-spin" /></div>
              ) : displayProps.length === 0 ? (
                <div className="text-center pt-10 text-gray-400">
                  <Building2 className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                  <p className="text-sm">No properties to display</p>
                </div>
              ) : displayProps.map((property, i) => (
                <div key={`${property.id}-${i}`}
                  className="group cursor-pointer bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all border border-gray-100 flex gap-0"
                  onClick={() => handleCardClick(property.id, property.isReal)}>
                  {/* Thumbnail */}
                  <div className="w-24 h-24 flex-shrink-0 overflow-hidden">
                    <img src={property.image} alt={property.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={e => { e.target.src = PLACEHOLDER; }} />
                  </div>
                  {/* Info */}
                  <div className="p-2.5 flex-1 min-w-0">
                    <h4 className="text-xs font-bold text-gray-900 line-clamp-2 group-hover:text-[#094f6f] leading-tight mb-1">
                      {property.name}
                    </h4>
                    <div className="flex items-center text-gray-400 text-[10px] mb-1">
                      <MapPin className="w-2.5 h-2.5 mr-0.5 flex-shrink-0" />
                      <span className="truncate">{property.location}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold text-[#094f6f]">
                        {property.price}<span className="text-[10px] font-normal text-gray-400">/{property.period}</span>
                      </span>
                      <div className="flex gap-1">
                        <button className="p-1 hover:text-red-500 transition-colors"
                          onClick={e => toggleFav(property.id, e)}>
                          <Heart className={`w-3 h-3 ${favorites.has(property.id) ? 'fill-red-500 text-red-500' : 'text-gray-300'}`} />
                        </button>
                        <button className="p-1 hover:text-[#094f6f] transition-colors"
                          onClick={e => share(property, e)}>
                          {copiedId === property.id
                            ? <CheckCircle className="w-3 h-3 text-green-500" />
                            : <Share2 className="w-3 h-3 text-gray-300" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: map fills remaining space */}
            <div className="w-full h-[400px] lg:flex-1 lg:h-auto rounded-2xl overflow-hidden shadow-md border border-gray-100">
              <Suspense fallback={<div className="h-full bg-gray-100 animate-pulse flex items-center justify-center"><Loader className="w-8 h-8 text-gray-400 animate-spin" /></div>}>
                <MapView
                  properties={displayProps.map(p => ({ ...p, city: p.city || p.location?.split(',')[0] || 'Melbourne' }))}
                  onPropertyClick={(p) => handleCardClick(p.id, p.isReal)}
                />
              </Suspense>
            </div>
          </div>
        )}

        {/* ── FOR YOU TAB ──────────────────────────────────────── */}
        {viewMode === 'foryou' && (
          <>
            {forYouLoading && (
              <div className="text-center py-16">
                <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="font-semibold text-gray-700">Analysing your requirements…</p>
                <p className="text-sm text-gray-400 mt-1">Claude AI is matching properties to your profile</p>
              </div>
            )}

            {!forYouLoading && forYouData && !forYouData.personalized && (
              <div className="text-center py-16">
                <Sparkles className="w-14 h-14 text-gray-200 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No requirements saved yet</h3>
                <p className="text-gray-400 text-sm mb-5 max-w-sm mx-auto">
                  Post your accommodation requirement in your Student Dashboard and we'll use AI to find the best matches for you.
                </p>
                <button onClick={() => navigate('/student-dashboard')}
                  className="bg-[#094f6f] text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-[#094f6f]/90 transition-colors">
                  Go to My Requirements
                </button>
              </div>
            )}

            {!forYouLoading && forYouData?.personalized && forYouData.rankings?.length > 0 && (
              <>
                {/* Requirement banner */}
                <div className="mb-8 p-4 bg-purple-50 border border-purple-100 rounded-2xl flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-purple-900 text-sm">
                      AI-ranked based on: "{forYouData.requirement?.title}"
                    </p>
                    <p className="text-purple-600 text-xs mt-0.5">
                      📍 {forYouData.requirement?.location} · A${forYouData.requirement?.budget}/wk · {forYouData.requirement?.roomType || 'Any room type'}
                    </p>
                  </div>
                  <button onClick={() => setForYouData(null)} className="ml-auto text-purple-400 hover:text-purple-600 text-xs underline whitespace-nowrap">Refresh</button>
                </div>

                {/* Ranked cards */}
                <div style={{ columns: 3, columnGap: '1.5rem' }} className="masonry-grid">
                  {forYouData.rankings.map((item, index) => {
                    const p = item.property;
                    if (!p) return null;
                    const prop = normalise({
                      ...p, images: p.images || [],
                      price_per_week: p.price_per_week, room_type: p.room_type,
                    }, true);
                    return (
                      <div key={index} style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}>
                        <Card
                          className="group cursor-pointer overflow-hidden border-0 shadow-md hover:shadow-2xl transition-all duration-300 rounded-2xl"
                          onClick={() => handleCardClick(p.id, true)}
                        >
                          <div className="relative overflow-hidden"
                            style={{ height: index % 3 === 0 ? '300px' : index % 3 === 1 ? '230px' : '265px' }}>
                            <img src={prop.image} alt={prop.name}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onError={e => { e.target.src = PLACEHOLDER; }} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                            {/* Match score badge */}
                            <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-xs font-bold text-white ${
                              item.match_score >= 80 ? 'bg-green-500' :
                              item.match_score >= 60 ? 'bg-yellow-500' : 'bg-gray-500'
                            }`}>
                              {item.match_score}% match
                            </div>

                            {/* Rank */}
                            <div className="absolute top-3 right-3 w-7 h-7 bg-black/50 text-white rounded-full flex items-center justify-center text-xs font-bold">
                              #{index + 1}
                            </div>

                            <div className="absolute bottom-3 right-3 bg-white/95 rounded-full px-3 py-1.5">
                              <span className="text-sm font-bold text-gray-900">
                                {prop.price}<span className="text-xs font-normal text-gray-500">/{prop.period}</span>
                              </span>
                            </div>
                            <div className="absolute bottom-3 left-3">
                              <span className="bg-[#094f6f]/85 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                                {prop.walkingDistance}
                              </span>
                            </div>
                          </div>

                          <CardContent className="p-4">
                            <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-purple-600 transition-colors line-clamp-1">
                              {prop.name}
                            </h3>
                            <div className="flex items-center text-gray-500 text-xs mb-2">
                              <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                              <span className="line-clamp-1">{prop.location}</span>
                            </div>
                            {/* AI match reason */}
                            <div className="bg-purple-50 text-purple-700 text-xs px-2.5 py-1.5 rounded-lg mb-3 line-clamp-2">
                              <span className="font-semibold">AI: </span>{item.match_reason}
                            </div>
                            <Button className="w-full bg-purple-600 text-white hover:bg-purple-700 transition-colors text-sm"
                              onClick={e => { e.stopPropagation(); handleCardClick(p.id, true); }}>
                              View Details
                            </Button>
                          </CardContent>
                        </Card>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ── REGULAR TABS ─────────────────────────────────────── */}
        {viewMode !== 'foryou' && loading && viewMode === 'all' && (
          <div className="flex justify-center py-20">
            <Loader className="w-8 h-8 text-[#094f6f] animate-spin" />
          </div>
        )}

        {/* Empty state for live listings */}
        {viewMode !== 'foryou' && isEmpty && (
          <div className="text-center py-20">
            <Building2 className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No live listings yet</h3>
            <p className="text-gray-400 text-sm mb-6">Be the first to list a property on RentalX!</p>
            <button onClick={() => setViewMode('featured')}
              className="text-[#094f6f] underline text-sm">View featured properties instead</button>
          </div>
        )}

        {/* Property Cards — Masonry / Pinterest layout */}
        {displayMode === 'grid' && viewMode !== 'foryou' && !loading && !isEmpty && (
          <>
            {/* CSS column masonry */}
            <div
              style={{
                columns: 3,
                columnGap: '1.5rem',
              }}
              className="masonry-grid"
            >
              {displayProps.map((property, index) => (
                <div
                  key={`${property.id}-${index}`}
                  style={{ breakInside: 'avoid', marginBottom: '1.5rem' }}
                >
                  <Card
                    className="group cursor-pointer overflow-hidden border-0 shadow-md hover:shadow-2xl transition-all duration-300 rounded-2xl"
                    onClick={() => handleCardClick(property.id, property.isReal)}
                    data-testid={`property-card-${property.id}`}
                  >
                    {/* Image — varying heights for masonry effect */}
                    <div
                      className="relative overflow-hidden"
                      style={{ height: index % 3 === 0 ? '320px' : index % 3 === 1 ? '240px' : '280px' }}
                    >
                      <img src={property.image} alt={property.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={e => { e.target.src = PLACEHOLDER; }} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                      {/* Action buttons */}
                      <div className="absolute top-3 right-3 flex gap-1.5">
                        <button className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                          onClick={e => toggleFav(property.id, e)}>
                          <Heart className={`w-4 h-4 ${favorites.has(property.id) ? 'fill-sky-500 text-sky-500' : 'text-gray-600'}`} />
                        </button>
                        <button className="w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                          onClick={e => share(property, e)}>
                          {copiedId === property.id
                            ? <CheckCircle className="w-4 h-4 text-green-600" />
                            : <Share2 className="w-4 h-4 text-gray-600" />}
                        </button>
                      </div>

                      {/* Price */}
                      <div className="absolute bottom-3 right-3 bg-white/95 rounded-full px-3 py-1.5">
                        <span className="text-sm font-bold text-gray-900">
                          {property.price}<span className="text-xs font-normal text-gray-500">/{property.period}</span>
                        </span>
                      </div>

                      {/* Uni tag */}
                      <div className="absolute bottom-3 left-3">
                        <span className="bg-[#094f6f]/85 text-white text-xs px-2.5 py-1 rounded-full font-medium">
                          {property.walkingDistance}
                        </span>
                      </div>

                      {property.isReal && (
                        <div className="absolute top-3 left-3 bg-green-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wide">
                          Live
                        </div>
                      )}
                    </div>

                    <CardContent className="p-4">
                      <h3 className="text-base font-bold text-gray-900 mb-1 group-hover:text-[#094f6f] transition-colors line-clamp-1">
                        {property.name}
                      </h3>
                      <div className="flex items-center text-gray-500 text-xs mb-3">
                        <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                        <span className="line-clamp-1">{property.location}</span>
                      </div>

                      {/* Room type badges */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {property.roomTypes.slice(0, 2).map((t, i) => (
                          <Badge key={i} variant="secondary" className="bg-sky-50 text-[#094f6f] text-xs">
                            {t}
                          </Badge>
                        ))}
                      </div>

                      {/* Amenities — only on taller cards */}
                      {index % 3 === 0 && property.amenities.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-3 text-gray-500 text-xs">
                          {property.amenities.slice(0, 3).map((a, i) => (
                            <span key={i} className="flex items-center gap-1">
                              <span className="text-[#094f6f]">{getAmenityIcon(a)}</span>{a}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Description snippet */}
                      {property.description && (
                        <p className="text-xs text-gray-400 line-clamp-2 mb-3 leading-relaxed">
                          {property.description}
                        </p>
                      )}

                      <Button
                        className="w-full bg-[#094f6f] text-white hover:bg-[#094f6f]/90 transition-colors text-sm"
                        onClick={e => { e.stopPropagation(); handleCardClick(property.id, property.isReal); }}>
                        View Details
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>

            {viewMode === 'all' && realProps.length < total && (
              <div className="text-center mt-12">
                <Button variant="outline" size="lg" onClick={loadMore} disabled={loadingMore}
                  data-testid="load-more-properties-btn"
                  className="text-[#094f6f] border-[#094f6f] hover:bg-sky-50 px-8 rounded-full">
                  {loadingMore ? (
                    <><Loader className="w-4 h-4 mr-2 animate-spin" /> Loading...</>
                  ) : (
                    <>Load More Properties <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            )}

            {/* Responsive masonry breakpoints */}
            <style>{`
              @media (max-width: 768px) { .masonry-grid { columns: 1 !important; } }
              @media (min-width: 769px) and (max-width: 1024px) { .masonry-grid { columns: 2 !important; } }
            `}</style>
          </>
        )}
      </div>
    </section>
  );
};

export default PropertyListings;
